import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, PencilSimple, Trash, Handshake, Coins } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import EmptyState from "@/components/EmptyState";

const EMPTY = { name: "", party_id: "", mode_name: "", commission_rate: "" };

export default function MediatorManagement() {
    const [mediators, setMediators] = useState([]);
    const [parties, setParties] = useState([]);
    const [modes, setModes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [commOpen, setCommOpen] = useState(false);
    const [commSaving, setCommSaving] = useState(false);
    const [commForm, setCommForm] = useState(null); // { mediatorId, mediatorName, amount, date, utr, remark }

    const load = async () => {
        setLoading(true);
        try {
            const [m, p, md] = await Promise.all([
                api.get("/mediators"),
                api.get("/parties"),
                api.get("/payment-modes"),
            ]);
            setMediators(m.data || []);
            setParties((p.data || []).filter((x) => x.active !== false));
            setModes((md.data || []).map((x) => x.name).filter(Boolean));
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const openAdd = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
    const openEdit = (m) => {
        setEditing(m);
        setForm({ name: m.name || "", party_id: m.party_id || "", mode_name: m.mode_name || "", commission_rate: String(m.commission_rate ?? "") });
        setDialogOpen(true);
    };

    const save = async () => {
        if (!form.name.trim()) return toast.error("Mediator name is required");
        if (!form.party_id) return toast.error("Pick a linked party");
        if (!form.mode_name.trim()) return toast.error("Payment mode name is required");
        const rate = Number(form.commission_rate);
        if (Number.isNaN(rate) || rate < 0 || rate > 100) return toast.error("Commission rate must be between 0 and 100");
        setSaving(true);
        try {
            const payload = { name: form.name.trim(), party_id: form.party_id, mode_name: form.mode_name.trim(), commission_rate: rate };
            if (editing) await api.patch(`/mediators/${editing.id}`, payload);
            else await api.post("/mediators", payload);
            toast.success(editing ? "Mediator updated" : "Mediator added");
            setDialogOpen(false);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const toggleActive = async (m) => {
        try { await api.patch(`/mediators/${m.id}`, { active: !m.active }); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const remove = async (m) => {
        if (!window.confirm(`Delete mediator "${m.name}"? This does NOT remove any posted commission rows.`)) return;
        try { await api.delete(`/mediators/${m.id}`); toast.success("Mediator deleted"); await load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const openAddCommission = (m) => {
        const allowed = m.applies_to_parties || [];
        setCommForm({
            mediatorId: m.id, mediatorName: m.name,
            allowed, linkedPartyId: m.party_id, linkedPartyName: m.party_name || partyName(m.party_id),
            party_id: allowed.length ? "" : (m.party_id || ""),
            amount: "", date: new Date().toISOString().slice(0, 10),
            utr: "Commission", remark: "Commission",
        });
        setCommOpen(true);
    };
    const saveCommission = async () => {
        if (!commForm) return;
        if (!commForm.party_id) return toast.error("Select which party to charge");
        const amt = Number(commForm.amount);
        if (Number.isNaN(amt) || amt <= 0) return toast.error("Enter a commission amount greater than 0");
        setCommSaving(true);
        try {
            await api.post(`/mediators/${commForm.mediatorId}/commission`, {
                amount: amt, date: commForm.date, utr: commForm.utr, remark: commForm.remark, party_id: commForm.party_id,
            });
            toast.success("Commission added to the ledger");
            setCommOpen(false); setCommForm(null);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setCommSaving(false); }
    };

    const partyName = useMemo(() => {
        const map = new Map(parties.map((p) => [p.id, p.name]));
        return (id) => map.get(id) || "—";
    }, [parties]);

    return (
        <div className="space-y-6" data-testid="mediators-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Link to="/admin" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-slate-800">
                        <ArrowLeft size={12} /> Back to Dashboard
                    </Link>
                    <div className="mt-1 flex items-center gap-2">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 text-white shadow-md">
                            <Handshake size={20} weight="fill" />
                        </div>
                        <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900">Mediators</h1>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">Payment providers with a commission rate. Commission is <b>no longer auto-posted</b> — click <b>Add Commission</b> on a mediator to post a commission entry manually to its linked party ledger.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={openAdd} data-testid="mediator-add-btn">
                        <Plus size={15} className="mr-1.5" /> Add Mediator
                    </Button>
                </div>
            </div>

            {mediators.length === 0 && !loading ? (
                <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                    <EmptyState icon={Handshake} title="No mediators yet" description="Add a mediator (e.g. Jitu) linked to its party ledger and payment mode, with a commission rate." />
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <table className="min-w-full text-sm" data-testid="mediators-table">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                            <tr>
                                <th className="px-4 py-3 text-left">Mediator</th>
                                <th className="px-4 py-3 text-left">Linked Party</th>
                                <th className="px-4 py-3 text-left">Payment Mode</th>
                                <th className="px-4 py-3 text-right">Commission %</th>
                                <th className="px-4 py-3 text-center">Status</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {mediators.map((m) => (
                                <tr key={m.id} data-testid={`mediator-row-${m.id}`} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 font-semibold text-slate-900">{m.name}</td>
                                    <td className="px-4 py-3 text-slate-700">{m.party_name || partyName(m.party_id)}</td>
                                    <td className="px-4 py-3"><span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700">{m.mode_name}</span></td>
                                    <td className="px-4 py-3 text-right font-mono font-semibold tabular-nums text-slate-900">{Number(m.commission_rate || 0).toFixed(2)}%</td>
                                    <td className="px-4 py-3 text-center">
                                        <button onClick={() => toggleActive(m)} data-testid={`mediator-toggle-${m.id}`}
                                            className={`rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${m.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                                            {m.active ? "Active" : "Inactive"}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <button onClick={() => openAddCommission(m)} data-testid={`mediator-add-commission-${m.id}`} className="inline-flex items-center gap-1 rounded-md border border-teal-200 bg-teal-50 px-2 py-1 text-xs font-semibold text-teal-800 hover:bg-teal-100" title="Add a manual commission entry"><Coins size={14} /> Add Commission</button>
                                            <button onClick={() => openEdit(m)} data-testid={`mediator-edit-${m.id}`} className="rounded p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-700" title="Edit"><PencilSimple size={15} /></button>
                                            <button onClick={() => remove(m)} data-testid={`mediator-delete-${m.id}`} className="rounded p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600" title="Delete"><Trash size={15} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent data-testid="mediator-dialog">
                    <DialogHeader><DialogTitle>{editing ? "Edit Mediator" : "Add Mediator"}</DialogTitle>
                        <DialogDescription className="sr-only">Create or edit a mediator, its linked party, payment mode and commission rate.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Name *</Label>
                            <Input data-testid="mediator-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Jitu" className="mt-1.5" />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Linked Party (ledger) *</Label>
                            <Select value={form.party_id} onValueChange={(v) => setForm({ ...form, party_id: v })}>
                                <SelectTrigger data-testid="mediator-party" className="mt-1.5"><SelectValue placeholder="Select party" /></SelectTrigger>
                                <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Payment Mode (e.g. "Recd From Jitu") *</Label>
                            <Input data-testid="mediator-mode" value={form.mode_name} onChange={(e) => setForm({ ...form, mode_name: e.target.value })} list="mediator-mode-options" placeholder="Recd From Jitu" className="mt-1.5" />
                            <datalist id="mediator-mode-options">{modes.map((n) => <option key={n} value={n} />)}</datalist>
                            <p className="mt-1 text-[11px] text-slate-500">Commission applies only to payments recorded with this exact mode.</p>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Commission Rate (%) *</Label>
                            <Input data-testid="mediator-rate" type="number" step="0.01" min="0" max="100" value={form.commission_rate} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })} placeholder="0.35" className="mt-1.5" />
                            <p className="mt-1 text-[11px] text-slate-500">Rate changes apply from now forward — already-posted months are never recalculated.</p>
                        </div>
                        {editing && Array.isArray(editing.rate_history) && editing.rate_history.length > 0 && (
                            <div data-testid="mediator-rate-history" className="rounded-md border border-slate-200 bg-slate-50 p-3">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Rate history</div>
                                <ul className="mt-1.5 space-y-1">
                                    {[...editing.rate_history].reverse().map((h, i) => (
                                        <li key={i} className="flex items-center justify-between text-xs">
                                            <span className="font-mono font-semibold text-slate-800">{Number(h.rate).toFixed(2)}%</span>
                                            <span className="text-slate-500">from {h.effective_from ? new Date(h.effective_from).toLocaleDateString("en-GB") : "—"}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="mediator-cancel">Cancel</Button>
                        <Button onClick={save} disabled={saving} data-testid="mediator-save">{saving ? "Saving…" : (editing ? "Update" : "Add")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={commOpen} onOpenChange={(o) => { setCommOpen(o); if (!o) setCommForm(null); }}>
                <DialogContent data-testid="mediator-commission-dialog">
                    <DialogHeader>
                        <DialogTitle>Add Commission{commForm ? ` — ${commForm.mediatorName}` : ""}</DialogTitle>
                        <DialogDescription className="sr-only">Manually post a commission entry to this mediator's linked party ledger.</DialogDescription>
                    </DialogHeader>
                    {commForm && (
                        <div className="space-y-4">
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Charge To Party *</Label>
                                <Select value={commForm.party_id} onValueChange={(v) => setCommForm({ ...commForm, party_id: v })}>
                                    <SelectTrigger data-testid="mediator-comm-party" className="mt-1.5"><SelectValue placeholder="Select party" /></SelectTrigger>
                                    <SelectContent>
                                        {(commForm.allowed && commForm.allowed.length
                                            ? commForm.allowed.map((ap) => <SelectItem key={ap.party_id} value={ap.party_id}>{ap.party_name}{ap.effective_from ? ` (from ${ap.effective_from.split("-").reverse().join("-")})` : ""}</SelectItem>)
                                            : <SelectItem value={commForm.linkedPartyId}>{commForm.linkedPartyName || "Linked party"}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {!(commForm.allowed && commForm.allowed.length) && (
                                    <p className="mt-1 text-[11px] text-slate-500">No party list set — charging the linked party. Set a party list under Mediator Commission → Parties.</p>
                                )}
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Commission Amount (₹) *</Label>
                                <Input data-testid="mediator-comm-amount" type="number" step="0.01" min="0" value={commForm.amount} onChange={(e) => setCommForm({ ...commForm, amount: e.target.value })} placeholder="e.g. 500" className="mt-1.5" autoFocus />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Date</Label>
                                <Input data-testid="mediator-comm-date" type="date" value={commForm.date} onChange={(e) => setCommForm({ ...commForm, date: e.target.value })} className="mt-1.5" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">UTR / Reference</Label>
                                <Input data-testid="mediator-comm-utr" value={commForm.utr} onChange={(e) => setCommForm({ ...commForm, utr: e.target.value })} className="mt-1.5" />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Remark</Label>
                                <Input data-testid="mediator-comm-remark" value={commForm.remark} onChange={(e) => setCommForm({ ...commForm, remark: e.target.value })} className="mt-1.5" />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setCommOpen(false); setCommForm(null); }} data-testid="mediator-comm-cancel">Cancel</Button>
                        <Button onClick={saveCommission} disabled={commSaving} data-testid="mediator-comm-save">{commSaving ? "Saving…" : "Add Commission"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
